import type { ChangeEvent, Coverage, Product, Report, ReportSnapshot } from "../../api/domain.ts";
import { rankProducts } from "./report-view.ts";
import { newestFirst } from "./history.ts";

/**
 * Turning the dashboard's four reads into the rows it shows.
 *
 * Nothing here scores anything: the score and the coverage of a product come
 * from an evaluation the server produced, either the newest locked report or one
 * live run. This module only picks which evaluation to trust and pairs it with
 * the product list.
 */

/** How many entities the change log panel asks the history route about. */
export const historyBudget = 6;

/** How many events the panel shows once they are merged. */
export const changeLogSize = 12;

/** Where the numbers on screen come from, so the panel can say which it is. */
export type ScoreOrigin = "locked" | "live" | "none";

export type ProductSummary = {
  product: string;
  name: string;
  active: boolean;
  score: number | null;
  coverage: Coverage | null;
};

/**
 * The newest report, preferring a locked one. A locked report reads from its
 * materialized snapshot, so it is the one number that cannot drift.
 */
export const newestReport = (reports: Report[]): Report | undefined => {
  const locked = reports.filter((report) => report.locked_at !== null);
  const candidates = locked.length > 0 ? locked : reports;
  return [...candidates].sort((left, right) => right.as_of - left.as_of)[0];
};

/**
 * Every product with the score and coverage the evaluation gives it. A product
 * the evaluation does not cover keeps a null score rather than a zero.
 */
export const productSummaries = (
  products: Product[],
  snapshot: ReportSnapshot | undefined,
): ProductSummary[] => {
  const ranked = snapshot === undefined ? [] : rankProducts(snapshot);
  const byId = new Map(ranked.map((entry) => [entry.product, entry]));

  return products
    .map((product) => {
      const evaluated = byId.get(product.id);
      return {
        product: product.id,
        name: product.name,
        active: product.active,
        score: evaluated?.score ?? null,
        coverage: evaluated?.coverage ?? null,
      };
    })
    .sort((left, right) => {
      if (left.score === right.score) return left.name.localeCompare(right.name);
      if (left.score === null) return 1;
      if (right.score === null) return -1;
      return right.score - left.score;
    });
};

/** One entity the change log panel asks the history route about. */
export type HistoryTarget = {
  entityType: string;
  id: string;
  label: string;
};

/**
 * The entities worth showing changes for, bounded so the panel never fans out
 * into an unpredictable number of requests.
 */
export const historyTargets = (
  products: Product[],
  questionnaires: { id: string }[],
  reports: Report[],
): HistoryTarget[] =>
  [
    ...products.map((product) => ({
      entityType: "product",
      id: product.id,
      label: product.name,
    })),
    ...questionnaires.map((questionnaire) => ({
      entityType: "questionnaire",
      id: questionnaire.id,
      label: questionnaire.id,
    })),
    ...reports.map((report) => ({
      entityType: "report",
      id: report.id,
      label: report.id,
    })),
  ].slice(0, historyBudget);

export type LabelledEvent = ChangeEvent & {
  /** The name the entity goes by on screen. */
  label: string;
};

/** The collected events, newest first and capped at what the panel shows. */
export const recentEvents = (events: LabelledEvent[], limit = changeLogSize): LabelledEvent[] =>
  newestFirst(events).slice(0, limit);
